import { useMutation, useQuery } from '@apollo/client';
import { Optional } from 'ts-toolbelt/out/Object/Optional';
import { v4 as uuid } from 'uuid';

import { AstroSmartLinkDomain, AstroSmartLinkInput } from '@/generated/gql/graphql';

import { DELETE_SMART_LINK_MUTATION } from './DeleteSmartLink.gql';
import { LOAD_SMART_LINKS_QUERY } from './LoadSmartLinks.gql';
import { UPDATE_SMART_LINK_MUTATION } from './UpdateSmartLink.gql';

export enum SmartLinkSaveStatus {
  DISABLED,
  UNSAVED,
  SAVED,
}

export interface UseSmartLinksProps {
  domain: AstroSmartLinkDomain;
}

type UpsertSmartLinkArgs = Optional<Omit<AstroSmartLinkInput, 'domain' | 'updatedAt'>, 'id'>;

export const useSmartLinks = ({ domain }: UseSmartLinksProps) => {
  const { data: smartLinks } = useQuery(LOAD_SMART_LINKS_QUERY, { variables: { domain } });
  const [update] = useMutation(UPDATE_SMART_LINK_MUTATION);
  const [remove] = useMutation(DELETE_SMART_LINK_MUTATION);

  const deleteSmartLink = async (id: string) => {
    return remove({
      variables: { id },
      refetchQueries: [{ query: LOAD_SMART_LINKS_QUERY, variables: { domain } }],
    }).catch(() => {
      throw Error('errors.delete.server');
    });
  };

  const upsertSmartLink = async (data: UpsertSmartLinkArgs | UpsertSmartLinkArgs[]) => {
    return await update({
      variables: { data: [data].flat().map((link) => ({ ...link, domain, id: link.id ? link.id : uuid() })) },
      refetchQueries: [{ query: LOAD_SMART_LINKS_QUERY, variables: { domain } }],
    }).catch(() => {
      throw Error('errors.update.server');
    });
  };

  return {
    smartLinks: smartLinks?.astronaut.smartLinks || [],
    deleteSmartLink,
    upsertSmartLink,
  };
};
